import { useEffect, useRef, useState } from 'react';
import { ArrowLeft } from '@phosphor-icons/react';
import { Link, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { canEditCampaign } from '../utils/can-edit-campaign.utils';
import { getCampaignHtml } from '../utils/campaign-content.utils';
import { formatCampaignContentSavedLabel } from '../utils/campaign-content-preview.utils';
import {
  deriveAutoPlainText,
  parseCampaignPlainMeta,
  parseCampaignTrackingSettings,
  upsertCampaignPlainMeta,
  upsertCampaignTrackingSettings,
  type CampaignTrackingSettings,
} from '../utils/campaign-content-meta.utils';
import { mapCampaignItemToCampaign } from '../utils/campaigns-mapper.utils';
import { useGetCampaignDetails, useUpdateCampaign } from '../hooks/use-campaigns';
import { useGetCampaignContentRest } from '../hooks/use-campaigns-rest';
import {
  CampaignEmailBuilder,
  type CampaignEmailBuilderHandle,
} from '../components/campaign-email-builder/campaign-email-builder';
import { CampaignContentEditorHeader } from '../components/campaign-content-editor-header/campaign-content-editor-header';
import { CampaignContentPreviewDialog } from '../components/campaign-content-preview-dialog/campaign-content-preview-dialog';
import { CampaignTestSendDialog } from '../components/campaign-test-send-dialog/campaign-test-send-dialog';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { useSidebar } from '@/components/ui-kit/sidebar';
import { Switch } from '@/components/ui-kit/switch';
import { Textarea } from '@/components/ui-kit/textarea';

export const CampaignContentEditorPage = () => {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const { toast } = useToast();
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar();
  const sidebarOpenBeforeEditorRef = useRef(sidebarOpen);
  const { campaign, isLoading, isError } = useGetCampaignDetails(campaignId);
  const { data: serverContent } = useGetCampaignContentRest(campaignId);
  const { mutateAsync: updateCampaign, isPending } = useUpdateCampaign();
  const builderRef = useRef<CampaignEmailBuilderHandle>(null);

  useEffect(() => {
    const sidebarOpenBeforeEditor = sidebarOpenBeforeEditorRef.current;
    setSidebarOpen(false);
    return () => {
      setSidebarOpen(sidebarOpenBeforeEditor);
    };
  }, [setSidebarOpen]);

  const [html, setHtml] = useState('');
  const [subtitleSavedAt, setSubtitleSavedAt] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [manualPlain, setManualPlain] = useState(false);
  const [plainText, setPlainText] = useState('');
  const [tracking, setTracking] = useState<CampaignTrackingSettings>(() =>
    parseCampaignTrackingSettings(null)
  );

  useEffect(() => {
    if (!campaign) return;
    setHtml(getCampaignHtml(campaign));
    setSubtitleSavedAt(campaign.LastUpdatedDate || campaign.CreatedDate || null);
    const plainMeta = parseCampaignPlainMeta(campaign.MetaTags);
    setManualPlain(plainMeta.manualPlain);
    setPlainText(plainMeta.plainText);
    setTracking(parseCampaignTrackingSettings(campaign.MetaTags));
  }, [campaign]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border bg-card p-6 text-sm text-muted-foreground">
        Loading editor…
      </div>
    );
  }

  if (isError || !campaign) {
    return (
      <div className="mx-auto flex max-w-[860px] flex-col items-center gap-3 rounded-lg border bg-card p-10 text-center shadow-e1">
        <h1 className="text-xl font-semibold">Campaign not found</h1>
        <p className="max-w-[360px] text-sm text-muted-foreground">
          This campaign may have been deleted, or the link is wrong.
        </p>
        <Button asChild className="bg-primary-600 text-white hover:bg-primary-700">
          <Link to="/campaigns">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to campaigns
          </Link>
        </Button>
      </div>
    );
  }

  const editable = canEditCampaign(mapCampaignItemToCampaign(campaign).status);
  const campaignName = campaign.Name?.trim() || 'Untitled campaign';
  const subject = campaign.CampaignMediaDetails?.[0]?.Subject?.trim() || '';

  const resolveLiveHtml = async (): Promise<string> => {
    const content = await builderRef.current?.exportContent();
    return content?.html?.trim() || html;
  };

  const handlePreview = () => {
    void (async () => {
      const live = await resolveLiveHtml();
      setPreviewHtml(live);
      setPreviewOpen(true);
    })();
  };

  const handleSave = async () => {
    if (!editable) return;
    const nextHtml = (await resolveLiveHtml()).trim();
    if (!nextHtml) {
      toast({
        variant: 'destructive',
        title: 'Nothing to save',
        description: 'Export HTML from the editor before saving.',
      });
      return;
    }

    const nextPlain = manualPlain ? plainText : deriveAutoPlainText(nextHtml);
    const existingMedia = campaign.CampaignMediaDetails?.[0];
    try {
      await updateCampaign({
        where: { ItemId: { eq: campaignId } },
        input: {
          CampaignMediaDetails: [
            {
              CampaignType: existingMedia?.CampaignType ?? 'Blank Template',
              Templet: nextHtml,
              ...(existingMedia?.MediaId ? { MediaId: existingMedia.MediaId } : {}),
              ...(existingMedia?.Subject ? { Subject: existingMedia.Subject } : {}),
            },
          ],
          MetaTags: upsertCampaignPlainMeta(campaign.MetaTags, {
            plainText: nextPlain,
            manualPlain,
          }),
        },
      });
      setHtml(nextHtml);
      setPlainText(nextPlain);
      const now = new Date().toISOString();
      setSubtitleSavedAt(now);
      toast({
        variant: 'success',
        title: 'Content saved',
        description: 'Your content was saved for this campaign.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save content',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleSaveTracking = async () => {
    if (!editable) return;
    try {
      await updateCampaign({
        where: { ItemId: { eq: campaignId } },
        input: {
          MetaTags: upsertCampaignTrackingSettings(
            upsertCampaignPlainMeta(campaign.MetaTags, {
              plainText,
              manualPlain,
            }),
            tracking
          ),
        },
      });
      toast({
        variant: 'success',
        title: 'Tracking settings saved',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not save tracking settings',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-5 pb-8">
      <CampaignContentEditorHeader
        title={`Edit content — ${campaignName}`}
        subtitle={formatCampaignContentSavedLabel(
          serverContent?.updatedAt || subtitleSavedAt,
          serverContent?.version
        )}
        onPreview={handlePreview}
        onTestSend={() => setTestSendOpen(true)}
        onSaveAsTemplate={() => builderRef.current?.openSaveAsTemplate()}
        backTo={{ label: 'Back to campaigns', to: '/campaigns' }}
      />

      {!editable ? (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3">
          <p className="flex-1 text-sm text-muted-foreground">
            Only draft campaigns can be edited. Duplicate the campaign to change its content.
          </p>
        </div>
      ) : null}

      <CampaignEmailBuilder
        ref={builderRef}
        html={html}
        name={campaignName}
        layout="design"
        showInlineActions={false}
        onSave={(_json, nextHtml) => setHtml(nextHtml)}
      />

      {/* Plain text (#144) — outside MailCraft; property of the content. */}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-6 shadow-e1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-700">
            <Switch
              checked={manualPlain}
              disabled={!editable}
              onCheckedChange={(checked) => {
                setManualPlain(checked);
                if (checked && !plainText.trim()) {
                  void (async () => {
                    const live = await resolveLiveHtml();
                    setPlainText(deriveAutoPlainText(live));
                  })();
                }
              }}
            />
            Author plain text manually
          </label>
          <span className="text-xs text-muted-foreground">
            {manualPlain
              ? 'Authored by you — no longer derived from the HTML.'
              : 'Derived from your HTML on every save.'}
          </span>
        </div>
        {manualPlain ? (
          <Textarea
            rows={6}
            disabled={!editable}
            placeholder="Plain-text version…"
            aria-label="Plain-text version of the email"
            value={plainText}
            onChange={(event) => setPlainText(event.target.value)}
          />
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1" />
        <Button
          type="button"
          className="bg-primary-600 text-white hover:bg-primary-700"
          disabled={!editable || isPending}
          onClick={() => {
            void handleSave();
          }}
        >
          {isPending ? 'Saving…' : 'Save content'}
        </Button>
      </div>

      {/* Link tracking & UTM (#151) */}
      <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6 shadow-e1">
        <h2 className="text-lg font-semibold text-foreground">Link tracking &amp; UTM</h2>
        {!editable ? (
          <p className="rounded-lg bg-muted px-3 py-2.5 text-xs text-foreground">
            Tracking can only be changed while this campaign is a draft.
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Switch
              checked={tracking.openTracking}
              disabled={!editable}
              onCheckedChange={(checked) =>
                setTracking((prev) => ({ ...prev, openTracking: checked }))
              }
            />
            Track opens
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Switch
              checked={tracking.clickTracking}
              disabled={!editable}
              onCheckedChange={(checked) =>
                setTracking((prev) => ({ ...prev, clickTracking: checked }))
              }
            />
            Track clicks
          </label>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="utm-source">UTM source</Label>
            <Input
              id="utm-source"
              disabled={!editable}
              value={tracking.utm.source}
              onChange={(event) =>
                setTracking((prev) => ({
                  ...prev,
                  utm: { ...prev.utm, source: event.target.value },
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="utm-medium">UTM medium</Label>
            <Input
              id="utm-medium"
              disabled={!editable}
              value={tracking.utm.medium}
              onChange={(event) =>
                setTracking((prev) => ({
                  ...prev,
                  utm: { ...prev.utm, medium: event.target.value },
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="utm-campaign">UTM campaign</Label>
            <Input
              id="utm-campaign"
              disabled={!editable}
              value={tracking.utm.campaign}
              onChange={(event) =>
                setTracking((prev) => ({
                  ...prev,
                  utm: { ...prev.utm, campaign: event.target.value },
                }))
              }
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Applied at send time — changing these does not require re-saving content or bumping its
          version.
        </p>
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={!editable || isPending}
            onClick={() => {
              void handleSaveTracking();
            }}
          >
            {isPending ? 'Saving…' : 'Save tracking settings'}
          </Button>
        </div>
      </div>

      <CampaignContentPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        html={previewHtml}
        plainText={manualPlain ? plainText : deriveAutoPlainText(previewHtml || html)}
      />
      <CampaignTestSendDialog
        open={testSendOpen}
        onOpenChange={setTestSendOpen}
        campaignId={campaignId}
        subject={subject}
      />
    </div>
  );
};
